/**
 * S1, the signed out landing: one page, one flight.
 *
 * THE CORRIDOR IS THE FOLD, by an explicit founder decision on 21 Aug 2026,
 * and every earlier version of this file argued the opposite at length. The
 * argument it lost is kept below rather than deleted, because it is a good one
 * and the next person deserves to see that it was answered.
 *
 *   It said: the address bar is the hero. A sentence in plain English becoming
 *   a live web address in five seconds is the entire pitch, rendered live, at
 *   8KB, with no media files. The corridor proves nothing about the product,
 *   so putting a mood piece in front of the proof trades the argument for the
 *   atmosphere.
 *
 *   The founder's answer: the mood piece is what buys the five seconds of
 *   attention the proof needs, and nothing is traded as long as the proof is
 *   the very next thing on the page.
 *
 * So chapter 1 is the flight with the approved copy box floating in the middle
 * of it, chapter 2 is the address bar directly underneath, and the copy that
 * used to sit on the old fold is GONE rather than repeated. There is exactly
 * one place on this page where the headline and the two actions live.
 *
 * THE JOURNEY IS NOW A DESCENT FROM THE FIRST FRAME. The visitor starts in the
 * air rather than on the ground: the flight, then the proof under it, then the
 * climb through the claims into clear air, and the ink band at the bottom. The last band still has no sky in it at all, and that is still
 * the argument for the layout: the waitlist is the one ink block on this page
 * because it is the ground. You do not sign up mid air.
 *
 * THE CLAIMS ARE DEMONSTRATED RATHER THAN STATED, which is the change that
 * reshaped chapters 3 and 4. This page argues that claims are cheap, and it
 * used to make its own claims in paragraphs of thirty odd words with
 * subordinate clauses in them. Each one had a working console component in this
 * repo that shows the thing instead. So chapter 3 is the real traffic band and
 * the real fix request, each with its approved heading unchanged above it, and
 * chapter 4's vocabulary table turns once instead of sitting still. The
 * demonstrations are LandingDemos.tsx; this file mounts them and gives them
 * room, and writes none of their copy.
 *
 * THE MIDDLE WAS CUT FOR A NOVICE ON 22 Aug 2026, and this is the shortest
 * statement of what changed and why. The founder read the whole middle of the
 * live page back to us as "confusing, and irrelevant for them to see". The
 * diagnosis was that the page argues like an engineer: a full build log with
 * timestamps, a compiler error code, and a line of commentary under every
 * demonstration explaining the thing the reader had just watched happen. Four
 * cuts followed, and all four are described where they landed.
 *   The raw build log went behind the fix request card's own disclosure, which
 *   deleted the fourth demonstration outright, because that demonstration
 *   existed to prove the doctrine the disclosure now proves in place.
 *   Every caption that narrated an animation went. One short line per
 *   demonstration, at most, and it has to state something the picture cannot.
 *   The vocabulary chapter said one thing three times; it says it once.
 *   The arrival's ten row ledger of masked hostnames became one line of ten
 *   lamps, and then the whole arrival chapter was removed the same evening,
 *   lamps included. The ruling and its residue are at the old chapter 5 slot.
 *
 * THE PAGE CARRIES NO ADDRESS BUT ITS OWN, since 23 Aug: the masked student
 * hostnames read as bleeped profanity and a parade of addresses read as "we
 * sell DNS" (backend feedback, founder ruling). The proof chip now rotates
 * the demo deploy's INVENTORY instead: the address on its true clock, then
 * the database, the files, the keys, in the product's words. The payoff
 * panel in chapter 2 lands the same argument with three quiet chips under
 * the address the moment it goes live. See LiveFeed.tsx for the history.
 *
 * THE FOOTER IS NOT IN THIS FILE. BareLayout renders SiteFooter under every
 * shell-less screen, so chapter 6's hairline and legal line came out of here
 * rather than being printed twice in a row. See the comment at the ground.
 *
 * THE ADDRESS BAR IS UNCHANGED, it has only moved. Five beats: a line types
 * itself, the panel body becomes the product's own provisioning checklist, the
 * panel morphs into browser chrome, the address fills in, and a status pill
 * flips to Live over a mono line that counts real seconds. Three decisions
 * inside it are worth defending.
 *
 * The typed line is a person's words, not a command: "a recipe site with photos
 * and a search box". A terminal command in a hero is the tell of every generic
 * infrastructure landing page, and this product's entire argument is that you
 * do not have to know the commands.
 *
 * The checklist rows are read out of lib/mock/scripts, which is the same script
 * the first build screen runs. The panel literally cannot drift from the
 * product, and it cannot flatter it either.
 *
 * The seconds counter counts from the moment the sequence lands in this browser
 * tab, never from a fabricated timestamp. It is the one number on the page and
 * it has to be true. TWO chapters read that one clock: the fold's proof chip
 * and the address bar that started it. The chip on the
 * fold therefore says nothing at all for the first five seconds of the visit,
 * because the address has not answered yet and the alternative is inventing a
 * number on the one line of this page that asks to be believed.
 *
 * ONE SKY OWNS THE WHOLE JOURNEY, founder ruling 22 Aug 2026, and it is the
 * rule that removed the hard line under the flight. There used to be a second
 * background on this page: a dimmed founder loop behind chapter 2, at low
 * opacity, under its own gradient. Two backgrounds meeting is a boundary
 * however softly either of them is painted, so the loop is unmounted and the
 * sky simply continues down through the proof, one veil deeper. Four moves make
 * the join disappear rather than merely soften it, and each is commented where
 * it lives: the second background is gone, a cloud plate straddles the seam,
 * the flight decelerates into its closing card while chapter 2's heading rises
 * out of the same sky, and the fold's own copy fades out over the last stretch
 * of the flight so nothing strikes through the top bar on the way down.
 *
 * NO SCROLL LIBRARY SHIPS. No Lenis, no GSAP, no smooth scroll hijack. Parallax
 * and every reveal run on native CSS scroll driven animations, which cost zero
 * bytes and run on the compositor, and every rule is authored so the finished
 * composition is the CSS default and the timeline only displaces it. Firefox,
 * an older iOS, and a reader who asked for no motion all get the same authored
 * page rather than a broken one.
 *
 * THE FLIGHT ITSELF IS THE ONE EXCEPTION, and it is an exception with a reason
 * written out in full at the top of CloudCorridor.tsx. The corridor's cards
 * ride one keyframe set at nine different phases and each of them WRAPS during
 * the journey; a native scroll driven animation has no wrap, its progress is
 * clamped to its range. So the corridor is driven by one passive listener and
 * one rAF gate writing a single custom property, which is also the number that
 * decides which cards are near enough to animate their contents. Still no
 * library, still one reader, and the section below hands it the track.
 */

import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { CloudField, VaporPass } from '../../components/patterns/CloudField';
import {
  FixRequestDemo,
  HealthDemo,
  VocabSweep,
} from '../../components/patterns/LandingDemos';
import { CorridorStill, SkyStill } from '../../components/patterns/LandingStills';
import { LiveFeed } from '../../components/patterns/LiveFeed';
import { StatusPill } from '../../components/patterns/StatusPill';
import { StepLedger } from '../../components/patterns/AsyncNarration';
import type { NarrationStep } from '../../components/patterns/AsyncNarration';
import { Wordmark } from '../../components/brand/Wordmark';

import { t } from '../../lib/i18n/t';
import { skyPrefersStill } from '../../lib/capability';
import { getToken, useSessionStore } from '../../lib/auth/session';
import { useNearViewport } from '../../lib/hooks/use-near-viewport';
import { FIRST_BUILD } from '../../lib/mock/scripts';
import { PUBLIC_DOMAIN } from '../../lib/mock/onboarding';
import { captureEvent } from '../../lib/analytics/posthog';

import { LiveSinceProvider, elapsedLine, useLiveSince } from './liveSince';
import {
  CLOUD_LAYERS,
  STREAM_MANIFEST,
  STREAM_MANIFEST_LEAN,
  streamImages,
} from './streamManifest';

import '../../styles/screens/landing.css';

/* -------------------------------------------------------- the two chunks */

/**
 * The sky and the corridor are the only heavy things on this page, and neither
 * of them is allowed to be in the entry bundle. Both are React.lazy, mounted
 * from this screen and from nowhere else, so a visitor who signs in and works
 * in the console never downloads a shader.
 *
 * The two mount gates are different because the two components have different
 * jobs.
 *
 * THE SKY IS THE ONLY BACKGROUND ON THE PAGE NOW, which raises what it is
 * carrying: every chapter from the flight to the arrival is printed on it, and
 * the descent veil in landing.css is what makes that a contrast bound rather
 * than an average. The three boxed copy scrims that used to do that job locally
 * came out with the loop.
 *
 * THE SKY HAS TWO GATES, AND THE CAPABILITY ONE COMES FIRST. skyPrefersStill
 * used to be answered inside WaveField, which meant a coarse pointer declined
 * the canvas only after the phone had fetched, parsed and evaluated the shader
 * chunk. The gate report flagged it. The question is the same question; it is
 * simply asked in front of the lazy() boundary now, so a phone never requests
 * the chunk at all. The second gate is the old one: the sky waits for the
 * address bar to land, per HeroBackdrop's rule that the loop plays once the
 * hero sequence has settled and never under it. That rule reads even better
 * from chapter 2 than it did from the fold, because now the one thing moving
 * on the fold is the corridor.
 *
 * The corridor is the fold, so its gate fires on the first observer callback
 * rather than after a scroll, and that is the point of keeping it: the chunk
 * request happens after the first paint, so the h1 is never behind it, and the
 * hook stays correct if this chapter ever moves again.
 *
 * Both fall back to their own still rather than to null or to a spinner. The
 * stills are eagerly imported above, which is why they are in their own module:
 * a lazy chunk's stylesheet arrives with the chunk, so a fallback defined
 * inside the chunk would paint unstyled. On the fold that fallback is doing
 * real work rather than covering a gap: CorridorStill is the composed frame, so
 * the first paint of this page is a finished picture with the copy box on it.
 */
const WaveField = lazy(() => import('../../components/patterns/WaveField'));
const CloudCorridor = lazy(() => import('../../components/patterns/CloudCorridor'));

/* ------------------------------------------------------------- the beats */

/**
 * The choreography, in real milliseconds, in one place.
 *
 * Durations normally live in the token layer, and every state transition in
 * this product still reads them from there. A hero sequence is the exception
 * on purpose: these are beats in a five second story, not the speed of a
 * hover, and they are handed to CSS as custom properties from this one object
 * so the animation and the state machine can never disagree about when a beat
 * ends.
 */
const BEATS = {
  type: 1600,
  holdAfterType: 480,
  perStep: 960,
  holdAfterList: 520,
  morph: 840,
  address: 1440,
} as const;

const CHECKLIST_KEYS = ['database.create', 'storage.create', 'build', 'domain.attach'];

const TYPED = 'nina-recipes';
const HERO_ADDRESS = `nina-recipes.${PUBLIC_DOMAIN}`;

/**
 * THE FOUNDER LOOP IS NOT MOUNTED ON THIS PAGE ANY MORE, and the three constants
 * that pointed at it are gone rather than left standing unused.
 *
 * They named an mp4, a webm and a poster, and they fed a HeroBackdrop sitting
 * behind chapter 2 at low opacity under a dim gradient. Founder ruling, 22 Aug
 * 2026: ONE SKY OWNS THE WHOLE JOURNEY. A second, differently lit backdrop
 * starting at the top of the proof chapter is what drew the hard line under the
 * flight, because a boundary between two backgrounds IS a boundary however
 * softly either one is painted. There is one background on this page now, the
 * sky, and it continues down through the proof rather than being replaced by
 * something else there.
 *
 * HeroBackdrop.tsx itself is untouched and is not deprecated. Sign in, the
 * waitlist and both graduation screens mount it, and it is still the component
 * that would carry the loop if the loop comes back somewhere it does not have
 * to share a screen with a sky.
 *
 * The one plate this page still reaches for out of the cloud manifest is the
 * mid deck, and it is used below to cover the seam this removal exposed.
 *
 * IT IS undefined RATHER THAN AN EMPTY STRING WHEN THE ENTRY IS MISSING, and
 * the seam simply does not render in that case. An empty string would reach the
 * stylesheet as `url()`, which is invalid at computed value time, which drops
 * mask-image back to its initial `none`, which paints the plate as a SOLID
 * rectangle of cloud ink across the middle of the page. A missing asset has to
 * fail to nothing, never to a grey band, on a page whose founder note this week
 * was about a grey band.
 */
const SEAM_PLATE = CLOUD_LAYERS[1]?.src;

const LIST_AT = BEATS.type + BEATS.holdAfterType;
const LIST_ENDS = LIST_AT + CHECKLIST_KEYS.length * BEATS.perStep;
const MORPH_AT = LIST_ENDS + BEATS.holdAfterList;
const ADDRESS_AT = MORPH_AT + BEATS.morph;
const LIVE_AT = ADDRESS_AT + BEATS.address;

type Phase = 'typing' | 'checklist' | 'browser' | 'live';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** One media query as a boolean, kept live. The phone flies a shorter corridor. */
function useMedia(feature: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(feature).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(feature);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [feature]);

  return matches;
}

/**
 * "Must this device be shown the still sky?", resolved BEFORE the first render
 * rather than after mount.
 *
 * The initializer is the load bearing part. Resolving this in an effect would
 * mean one render in which the answer is "no", and one render is all it takes
 * for React.lazy to fire the import, which is the exact cost the gate exists to
 * avoid. The listener afterwards is only there so a reader who flips the OS
 * motion setting does not have to reload the page.
 */
function useSkyStill(): boolean {
  const [still, setStill] = useState(skyPrefersStill);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const resolve = () => setStill(skyPrefersStill());
    motion.addEventListener('change', resolve);
    return () => motion.removeEventListener('change', resolve);
  }, []);

  return still;
}

/* ------------------------------------------------- the proof panel, chapter 2 */

function AddressBar() {
  const [phase, setPhase] = useState<Phase>('typing');
  const [done, setDone] = useState(0);
  const { seconds, start } = useLiveSince();

  // Behind a ref so a provider re-render cannot restart the five second
  // sequence by handing this effect a new function identity.
  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPhase('live');
      setDone(CHECKLIST_KEYS.length);
      startRef.current();
      return;
    }

    const timers: number[] = [];
    const at = (ms: number, run: () => void) => timers.push(window.setTimeout(run, ms));

    at(LIST_AT, () => setPhase('checklist'));
    CHECKLIST_KEYS.forEach((_, index) => {
      at(LIST_AT + (index + 1) * BEATS.perStep, () => setDone(index + 1));
    });
    at(MORPH_AT, () => setPhase('browser'));
    at(LIVE_AT, () => {
      startRef.current();
      setPhase('live');
    });

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, []);

  const steps: NarrationStep[] = useMemo(
    () =>
      CHECKLIST_KEYS.map((key, index) => {
        const step = FIRST_BUILD.steps.find((candidate) => candidate.key === key);
        const settled = phase === 'browser' || phase === 'live';
        return {
          label: t(step ? step.label : key),
          status: settled || index < done ? 'succeeded' : index === done ? 'running' : 'pending',
        };
      }),
    [done, phase],
  );

  const caption =
    phase === 'typing'
      ? t('Give your app a name')
      : phase === 'checklist'
        ? t('What SV Cloud did')
        : t('Your project is Live');

  return (
    <div className="ab-wrap">
      <div
        className="ab"
        data-phase={phase}
        style={
          {
            '--ab-chars': TYPED.length,
            '--ab-addr-chars': HERO_ADDRESS.length,
            '--ab-type': `${BEATS.type}ms`,
            '--ab-morph': `${BEATS.morph}ms`,
            '--ab-address': `${BEATS.address}ms`,
          } as React.CSSProperties
        }
      >
        <p className="ab__caption mono">{caption}</p>

        <div className="ab__body">
          {phase === 'typing' ? (
            <p className="ab__prompt mono">
              <span className="ab__typed">{TYPED}</span>
              <span className="ab__rule" aria-hidden="true" />
            </p>
          ) : null}

          {phase === 'checklist' ? (
            <div className="ab__list">
              <StepLedger steps={steps} />
            </div>
          ) : null}

              {/* WHAT CAME WITH IT, and it is the platform's whole argument
                  landed at the payoff. An address alone is a claim any host
                  can make; the row under it says the deploy arrived holding
                  a database and file storage that are already connected,
                  which is the part a novice would otherwise spend a week
                  wiring. Three product words and one sentence: the panel had
                  the empty space, and this is what the space was for. It
                  arrives WITH the live state, never before it, because the
                  inventory of a deploy that has not finished is a promise
                  rather than a fact. */}
              {phase === 'live' ? (
                <span className="ab__got">
                  <span className="ab__got-row">
                    <span className="ab__got-item mono">{t('Web address')}</span>
                    <span className="ab__got-item mono">{t('Database')}</span>
                    <span className="ab__got-item mono">{t('Files')}</span>
                    <span className="ab__got-item mono">{t('More to Come...')}</span>
                  </span>
                </span>
              ) : null}

          {phase === 'browser' || phase === 'live' ? (
            <div className="ab__browser">
              <span className="ab__nav" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
                  <path
                    d="M10 3.5 5.5 8l4.5 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
                  <path
                    d="M6 3.5 10.5 8 6 12.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>

              <span className="ab__field">
                <span className="ab__lock" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="11" height="11" focusable="false">
                    <rect
                      x="3.5"
                      y="7"
                      width="9"
                      height="6.5"
                      rx="1.2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M5.8 7V5.4a2.2 2.2 0 0 1 4.4 0V7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                  </svg>
                </span>
                <span className="ab__addr mono">{HERO_ADDRESS}</span>
              </span>

              {phase === 'live' ? (
                <span className="ab__pill">
                  <StatusPill value="live" size="sm" live />
                </span>
              ) : null}

            </div>          ) : null}
        </div>
      </div>

      <p className="ab-wrap__meter mono">
        {phase === 'live' && seconds !== null ? elapsedLine(seconds) : ''}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- screen */

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

function LandingPage() {
  const navigate = useNavigate();
  const sessionStatus = useSessionStore((s) => s.status);
  const isLoggedIn =
    sessionStatus === 'signed-in' || (!USE_MOCK && sessionStatus === 'loading' && !!getToken());

  // heroLive is derived from the clock rather than from a second piece of
  // state. There is one fact here, "the address has answered", and the page
  // gates the sky, the wind and the founder loop on it.
  const { seconds } = useLiveSince();
  const heroLive = seconds !== null;

  const [flightRef, flightNear] = useNearViewport<HTMLElement>();
  const lean = useMedia('(max-width: 720px)');
  const skyStill = useSkyStill();

  /**
   * The waitlist sentinel, and it is a claim the page can check.
   *
   * The sky is one fixed canvas, so an IntersectionObserver on the canvas
   * itself thinks it is on screen for the entire visit. This watches the ink
   * band instead and stops the render loop outright the moment the band covers
   * the viewport. Not paused visually, not throttled: zero frames. Not one
   * pixel on this page is animating while somebody is typing their email
   * address.
   *
   * THE SECOND CONDITION IS THE FOOTER, and it was added the day the footer
   * arrived. The ink band used to be the last thing in the document, so "the
   * band covers three quarters of the viewport" and "there is no sky on screen"
   * were the same sentence. SiteFooter now sits under it, three hundred and
   * seventy pixels of opaque type on desktop and four hundred and eighty on a
   * phone. Measured at the bottom of the page: at 1440x900 the band still fills
   * 82 percent of the viewport and the loop stayed stopped, but at 1280x700 it
   * fills 65 percent and at 390x844 it fills 60, and on both of those the loop
   * started again to paint a sky nobody can see through an opaque footer.
   *
   * So the test is now "the band covers the viewport OR the reader is past the
   * band", and past is read off the same entry: a top edge at or above zero
   * means everything on screen is the ink band and whatever follows it, and
   * nothing that follows it has sky behind it. The 0.75 threshold is kept
   * because it is what makes the callback fire on both edges of the crossing,
   * which is what lets one rule answer both directions of travel.
   */
  const groundRef = useRef<HTMLElement>(null);
  const [skyCovered, setSkyCovered] = useState(false);
  useEffect(() => {
    const node = groundRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const fills = entry.intersectionRatio > 0.75;
        const past = entry.boundingClientRect.top <= 0;
        setSkyCovered(fills || past);
      },
      { threshold: [0, 0.75, 1] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The phone flies nine of the sixteen, because sixteen cards on a 375px rail
  // is a smear rather than a stream. Which nine, and the two properties the
  // shorter list was solved for rather than truncated to, are argued in
  // streamManifest.ts; all three runners and both readable panels survive the
  // cut. The breakpoint is the same 720px pickDensity() uses for lean geometry,
  // so the card list and the card count change together.
  const cards = useMemo(
    () => streamImages(lean ? STREAM_MANIFEST_LEAN : STREAM_MANIFEST),
    [lean],
  );

  useEffect(() => {
    captureEvent('marketing_landing_viewed', {
      is_logged_in: Boolean(isLoggedIn),
    });
  }, [isLoggedIn]);

  return (
    <div className="lp">
      {/* CHAPTER 0. The horizon line. The bar has no ground on the fold and
          gains one by the time the horizon has gone, then keeps it. */}
      <nav className="lp__nav" aria-label={t('Site')}>
        <Link className="lp__brand" to="/" aria-label={t('SV Cloud')}>
          <Wordmark />
        </Link>
        {/* ONE LINK, BECAUSE ONE OF THEM WENT SOMEWHERE. The bar used to carry
            a Documentation item that was a <span> rather than an <a>: a word
            styled like a destination, in the top right of a page whose whole
            argument is that nothing here is dressed up as more than it is. It
            comes back the day there are docs to point at, as a Link. */}
        <div className="lp__nav-links">
          {isLoggedIn ? (
            <Link className="lp__nav-link" to="/projects">
              {t('Go to project')}
            </Link>
          ) : (
            <Link
              className="lp__nav-link"
              to="/sign-in"
              onClick={() => captureEvent('marketing_sign_in_clicked', { source: 'landing_nav' })}
            >
              {t('Sign in')}
            </Link>
          )}
        </div>
      </nav>

      {/* THE SKY. One fixed canvas for the whole document, and the clouds in
          front of it, and every chapter in front of both.

          Two gates, and the capability one is first so a phone never fetches
          the shader chunk it was always going to refuse. The second is the old
          one: it mounts once the address bar in chapter 2 has finished talking,
          for the reason HeroBackdrop wrote down, that the doctrine allows one
          thing moving per screen. On the fold that one thing is the corridor,
          so the shader waiting is now the correct behaviour twice over rather
          than merely a cheap one.

          It stops dead at the waitlist. See the sentinel above. */}
      {/* data-live is not styling. It is the claim, written where a reviewer or
          a QA run can read it: "stopped" means the render loop is not being
          asked for frames because the ink band is covering the sky. */}
      <div className="lp__sky" data-live={skyCovered ? 'stopped' : 'running'} aria-hidden="true">
        {heroLive && !skyStill ? (
          <Suspense fallback={<SkyStill />}>
            <WaveField active={!skyCovered} />
          </Suspense>
        ) : (
          <SkyStill />
        )}
        <span className="lp__sky-lift" />
        {/* The deck stops on the same sentinel as the shader, and for the same
            reason. Wind is the one motion on this page that runs on a clock
            rather than on the reader, so behind an opaque ink band it is three
            composited layers being repainted forever to show nobody anything.
            Stopping it here is what makes chapter 5's "no motion" literal
            rather than merely invisible. */}
        <CloudField wind={heroLive && !skyCovered} />

        {/* THE DESCENT, and it is the other half of "one sky owns the whole
            journey". The dimmed founder loop that used to start at the top of
            the proof chapter is gone, so the sky is now the only ground under
            every chapter from the flight to the arrival, and it has to do two
            jobs the loop was doing badly: it has to change as the reader drops
            out of the flight, and it has to be a floor a paragraph can sit on.

            One veil does both. It is the page's own ground value laid over the
            sky, at zero on the fold and at strength from the seam onward, so
            descending the page deepens the sky toward the ground in both themes
            rather than swapping it for another picture. Three local copy scrims
            came out when this went in: the boxed radials behind the proof
            heading, the claim headings and the clear air lede, each of which
            was a rectangle a reader could see the edge of. The argument for
            that trade is written over the rule in landing.css.

            IT IS THE LAST CHILD OF THE SKY, WHICH IS THE LOAD BEARING PART. A
            veil is a contrast floor only if nothing that can appear under a
            paragraph is drawn on top of it, and the cloud deck can appear under
            a paragraph. So the shader, the climb and the clouds are all
            underneath this, in that order, and this is what a reader's text
            actually sits on. */}
        <span className="lp__sky-deep" />
      </div>

      {/* CHAPTER 1. THE FLIGHT, AND IT IS THE FOLD. A viewport pinned inside a
          tall track, and the only place the corridor appears on this page.

          THE READER FLIES IT. The founder's ruling on 21 Aug 2026 was "maybe
          only move the gallery when scrolling only", and this section is where
          that lands: the track's own scroll progress IS corridor progress.
          Scrolling forward flies forward, stopping stops the flight, scrolling
          back flies back. The timer that used to hold the fold for a beat and
          then play one journey is gone, and so is the begin prop that told it
          which of the two placements it was in.

          IT STILL RUNS ONCE AND ARRIVES, which is the correction to "we keep
          running through the clouds". The flights that read as expensive are
          the ones that END; a visible loop reclassifies the whole thing from a
          title sequence to a screensaver. The journey is exactly one cycle
          long, so the frame at the top of the track and the frame at the bottom
          of it are the same composed picture, and a reader who has not scrolled
          yet is looking at that picture rather than at an empty axis.

          THE TRACK IS THE THING THAT MOVES, so the track is what the corridor
          reads. The corridor lives inside the sticky frame, whose rect stops
          moving the instant the chapter pins, which is precisely the stretch
          the flight has to happen in. Handing it flightRef is the same
          reasoning landing.css writes down for naming its view-timeline on the
          track rather than on the pinned frame.

          The cards are evidence rather than a mood board: runner silhouettes
          interleaved with live terminal panels, cloud plates and real product
          screens in strict rotation, so no two of a kind are ever adjacent, and
          the only saturated pixels in the whole flight are the status lamps
          inside the screens and the panels.

          THE LCP IS THE h1 IN THIS FILE, not anything in the corridor chunk.
          The copy box is a plain element in the entry bundle, rendered on the
          first pass; the corridor mounts underneath it afterwards with
          CorridorStill standing in, so the fold is a finished picture from the
          first frame and never a hole that fills in. */}
      <section className="lp__flight" ref={flightRef} aria-labelledby="lp-fold-title">
        <div className="lp__flight-stick">
          <span className="lp__flight-uv" aria-hidden="true" />

          {/* WHAT THE h1 IS AND IS NOT, MEASURED RATHER THAN CLAIMED.
              Chrome ranks the largest contentful paint by painted AREA, and it
              counts a photograph the corridor is flying past. On a 1440 window
              the numbers are: the h1 is the first contentful paint of this
              document at 304ms and 70,564px, and a founder card at reading size
              is 331x360, which is 108,214px after clipping. The card takes the
              title 80ms later and there is no arrangement of a photographic
              fold in which it does not, short of shrinking the flight to a
              band with two hard edges or pushing the headline a rung past the
              top of the type scale. Both would be changing the picture the
              founder approved in order to win a metric about it.

              So what is actually guaranteed here, and it is the part that
              matters: the h1 is plain text in the entry bundle, painted on the
              first pass, opaque, at its final position, on no timeline, and it
              never waits on the corridor chunk. The corridor mounts underneath
              it afterwards. The measured LCP TIME is 0.38s either way, so the
              metric's value is unharmed; only which element gets the label
              changes. Building the fallback without its manifest was tried and
              reverted: it removed the 142,563px still card, the next moving
              card took the title anyway, and the fold lost real photographs for
              its first four hundred milliseconds to buy nothing. */}
          {flightNear ? (
            <Suspense fallback={<CorridorStill images={cards} />}>
              <CloudCorridor images={cards} track={flightRef} />
            </Suspense>
          ) : (
            <CorridorStill images={cards} />
          )}

          <VaporPass pass="wipe" />

          {/* The approved box, floating in the centre of the flight. It carries
              its own copy shield, which is the same pattern and the same token
              the statement bands use: text on this page never sits on moving
              imagery, and the shield is what makes the contrast under it a
              bound rather than an average. */}
          <header className="lp__fold">
            <h1 className="lp__title" id="lp-fold-title">
              {t("Put your app on the internet, and know that it's fine.")}
            </h1>
            <p className="lp__lede">
              {t(
                'SV Cloud gives your code a web address, a database and file storage. Then it tells you, in words you already use, whether any of it is working.',
              )}
            </p>
            <div className="lp__cta">
              {isLoggedIn ? (
                <Button variant="solid" size="lg" onClick={() => navigate('/projects')}>
                  {t('Go to project')}
                </Button>
              ) : (
                <>
                  <Button variant="solid" size="lg" onClick={() => navigate('/waitlist')}>
                    {t('Join the waitlist')}
                  </Button>
                  <Link className="link-inline lp__cta-link" to="/sign-in">
                    {t('I already have an account')}
                  </Link>
                </>
              )}
            </div>

            {/* ONE PROOF LINE, and what rotates in it changed on 23 Aug:
                the demo address on its true clock, then the deploy's own
                inventory (database, files, keys) in the product's words.
                Never another hostname: the full reasoning and the history of
                the ten student apps this line used to carry are at the top
                of LiveFeed.tsx. */}
            <div className="lp__chips">
              <LiveFeed rotate />
            </div>
          </header>
        </div>
      </section>

      {/* THE SEAM, AND IT IS A CLOUD RATHER THAN A LINE.

          The flight is a viewport pinned in a tall track and the proof is an
          ordinary block under it, so the document has a hard boundary between
          them at the bottom of the track, and no amount of softening either
          side removes it: it is where one thing stops and another starts. The
          founder's answer on 22 Aug 2026 was not to soften the line but to put
          something over it. This is that something.

          One wide plate, straddling the join, drawn over the last screenful of
          the flight and under the first screenful of the proof. It is the same
          mask-mode luminance plate the deck flies, filled from the same cloud
          token, so it is the weather rather than a divider drawn to look like
          weather, and it restyles with everything else from one value. It rides
          its own view progress at a fraction of the page's speed, which is what
          makes exiting the flight read as dropping THROUGH cloud rather than as
          scrolling past a picture of some.

          IT IS ZERO PIXELS TALL. The element is a seam in the document flow and
          the plate inside it bleeds half a screen either way, so it moves the
          layout by nothing at all and can be deleted with no consequence to any
          chapter's geometry. It sits between the two sections in source order,
          which is exactly what puts it over the one and under the other with no
          z-index arithmetic: both chapters are on the same rung, so document
          order decides, and document order here is the truth. */}
      {SEAM_PLATE ? (
        <div className="lp__deck" aria-hidden="true">
          <span
            className="lp__deck-plate"
            style={{ '--plate': `url(${SEAM_PLATE})` } as React.CSSProperties}
          />
        </div>
      ) : null}

      {/* CHAPTER 2. THE PROOF, and it is the very next thing on the page.
          A sentence in plain English becoming a live web address in five
          seconds, rendered live, at 8KB, with no media files.

          THERE IS NO SECOND BACKDROP HERE ANY MORE. The dimmed founder loop
          that used to sit behind this chapter is gone, on the founder's ruling
          that one sky owns the whole journey. What is behind this chapter is
          the same sky that is behind the flight, one veil deeper. See the
          descent veil in the sky above, and the constants that used to point at
          the loop, which are a gravestone near the top of this file.

          THE HEADING RISES OUT OF THAT SKY, which is the third of the four seam
          moves. It is the only motion in this chapter that runs on the reader:
          the address bar's own sequence started on load and has settled long
          before anybody scrolls here. So the flight decelerates into its closing
          card, the cloud passes over the join, and this sentence lifts out of
          the same sky underneath it. One handoff, three parts, no cut. */}
      <section className="lp__proof" aria-labelledby="lp-proof">
        {/* One line, and it states the fact rather than asking for a show. The
            sequence starts when this component mounts, which is on load, so a
            reader who spent eight seconds on the fold arrives to a settled
            panel and an honest counter. A title that said "watch" would be
            promising something the page cannot guarantee it still has. */}
        <div className="lp__proof-say">
          <h2 className="lp__section-title" id="lp-proof">
            {t('Built for AI agents, not engineers.')}
          </h2>
        </div>

        <AddressBar />
      </section>

      {/* CHAPTER 3. The lift, and it is TWO demonstrations now. The vapor still
          makes its first pass behind them, which is still the first moment
          something goes PAST the reader rather than with them.

          WHY THE PROSE WENT, ORIGINALLY. This band used to be three headline
          plus paragraph blocks of thirty odd words each, with subordinate
          clauses, on a page whose entire thesis is that claims are cheap and
          checkable things are not. Every one of the three had a working console
          component sitting in this repo that DEMONSTRATES it, so the paragraphs
          went and the components arrived.

          WHY THE THIRD ONE WENT, WHICH IS THE CHANGE OF 22 Aug 2026. The
          founder read the middle of this page back to us as "confusing, and
          irrelevant for them to see", where them is a person who has never
          deployed anything. The third demonstration was headed "Nothing is
          hidden behind a friendlier word" and it proved that claim by opening a
          disclosure onto a project identifier plate, with a hairline drawn
          across the card and a mono line reading "nothing above this line
          moves". Every word of that is true and every word of it is written for
          somebody who already knows what a disclosure is and why they should
          care that it does not reflow.

          The claim did not go. It moved into the demonstration above it, where
          the raw build log now lives behind that card's own "Show technical
          details", which is the same real disclosure doing the same real thing
          on something a novice actually wants: the honest machine text, kept
          one press away from a sentence in English. One press, one claim, one
          less section, and the reader who never presses it is not asked to
          admire a mechanism.

          THE TWO REMAINING HEADINGS ARE UNCHANGED, word for word.

          ONE EFFECT OWNS EACH SCREEN, which is why nothing in this chapter is
          on a scroll timeline. The band used to fade each plate in on its own
          view() progress, and that was right for still paragraphs. A plate that
          fades itself in while the demonstration inside it is already animating
          is two effects arguing about one screen, and the reader loses the first
          beat of the thing being proved. Each demo owns an observer of its own
          and gates itself, so the chapter's whole motion budget is spent on the
          claim.

          THE HEADING RAIL STILL ALTERNATES SIDES, and with two demonstrations
          it is a left and a right rather than a pattern that has to be counted.
          It only splits into two columns on a window wide enough that both
          halves get room: below that the heading is a line above its
          demonstration and the demonstration takes the full measure. */}
      <section className="lp__band" aria-label={t('What SV Cloud does')}>
        <VaporPass pass="climb" />

        <article className="lp__demo" data-rail="start">
          <div className="lp__demo-say">
            <h2 className="lp__demo-title">{t('Your app tells you whether it is fine.')}</h2>
          </div>
          <div className="lp__demo-stage">
            <HealthDemo />
          </div>
        </article>

        <article className="lp__demo" data-rail="end">
          <div className="lp__demo-say">
            <h2 className="lp__demo-title">
              {t('When something breaks, you get a fix request to paste.')}
            </h2>
          </div>
          <div className="lp__demo-stage">
            <FixRequestDemo />
          </div>
        </article>
      </section>

      {/* CHAPTER 4. Clear air. The calmest surface on the page and the
          emptiest: the reader is above the weather. The one thing that moves
          here is the table, once, as a single object. Why it may never move
          row by row is written over the mount below. */}
      <section className="lp__vocab" aria-labelledby="lp-vocab">
        <div className="lp__vocab-say">
          <h2 className="lp__section-title" id="lp-vocab">
            {t('Seven words, and then you know the product.')}
          </h2>
          {/* THE ONE LINE THAT SURVIVES, AND IT SURVIVES HERE. The table under
              it used to carry two more of its own: "Other clouds have many
              words for one thing. We picked one." and "Seven words. There is no
              glossary to go and learn first." Three sentences saying one thing,
              stacked, on the calmest chapter of the page. Founder ruling, 22
              Aug 2026: one survives, once. It is this one, because it is the
              one a reader meets BEFORE the table rather than after it, which is
              the only position where a sentence about a table is doing any
              work at all. */}
          <p className="lp__section-lede">
            {t(
              'Every screen uses the word on the left. Nothing in the product will ever say a word on the right, and there is no glossary to go and learn first.',
            )}
          </p>
        </div>

        {/* The seven words, in the product's own Table primitive, turning once
            as the chapter arrives. The table is unchanged as an object: what
            moved is that the crowded right hand column is what is present
            first, at lead weight, and it falls back to muted as the plain left
            word settles into the lead. One transition, once, for all fourteen
            cells at the same instant.

            NEVER ROW BY ROW. A seven row ledger that reveals itself in
            sequence is a slot machine, and this product's claim is that
            nothing is hidden. That was the rule when the table was static and
            it is the rule now that it turns.

            The vocabulary itself lives in the component. It was a local array
            here and a mirrored one there, which is one list of seven words
            with two places to edit it and no mechanism to keep them level. */}
        <VocabSweep />
      </section>

      {/* THE ARRIVAL CHAPTER IS GONE, founder ruling 22 Aug 2026, late. It
          carried the Bangkok overline, the three people paragraph, the third
          reading of the one clock, and the cohort lamp strip. The ruling was
          to remove the whole thing, and the page reads cleaner for it: the
          vocabulary chapter now hands straight to the ground, and the two
          honesty devices that survive are the two that were always doing the
          work, the chip's rotation up top and the address bar's true clock.
          The chip still rotates through the ten student apps, so the cohort
          proof did not leave the page, it left the basement. If a founders
          chapter returns one day it returns as its own decision, not as a
          revival of this one. */}

      {/* CHAPTER 5. The ground.

          A full bleed band of ink with one hairline across its top. No sky, no
          clouds, no gradient, no motion. The sky is COVERED rather than faded,
          and the WebGL loop is stopped outright while this band is on screen.

          CHAPTER 6 IS THE SITE FOOTER NOW, and it is not in this file. This
          band used to end with .lp__horizon, a hairline and the company's
          English legal line in mono. BareLayout renders SiteFooter under every
          shell-less screen, and that footer carries the same legal line one
          element below where this one was printing it, so the page said the
          same sentence twice in a row. The line lives in exactly one place
          now, the place that shows it on the waitlist and the privacy page as
          well. See SiteFooter.tsx. */}
      <section className="lp__ground" ref={groundRef}>
        <div className="lp__close">
          <h2 className="lp__section-title">{t('We are letting people in a few at a time.')}</h2>
          <p className="lp__section-lede">
            {t(
              'Small enough that we can watch every first deploy and fix what goes wrong. Leave your email and we will tell you when your turn comes.',
            )}
          </p>
          <Button
            variant={isLoggedIn ? 'solid' : 'outline'}
            size="lg"
            onClick={() => navigate(isLoggedIn ? '/projects' : '/waitlist')}
          >
            {isLoggedIn ? t('Go to project') : t('Join the waitlist')}
          </Button>
        </div>
      </section>
    </div>
  );
}

export default function Landing() {
  // The clock is above the page rather than inside the address bar panel,
  // because three chapters read it now, the fold's proof chip included, and
  // they have to read the same one.
  return (
    <LiveSinceProvider>
      <LandingPage />
    </LiveSinceProvider>
  );
}
